import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Table,
} from "react-bootstrap";
import { Row, Col, Label } from "reactstrap";
import Select from "react-select";
import {
  useLazyGetCartDetailsByIdQuery,
  useUpdateCartDetailsMutation,
} from "../../../../redux/services/cartSearchService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFloppyDisk,
  faSpinner,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ButtonComponentV2 from "../../../../components/ButtonComponentV2";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  FirebaseRestaurantItem,
  useRestaurantDetails,
} from "../../../../hooks/useRestaurantDetails";

interface ItemOptionType {
  id: string;
  displayName: string;
  imageUrl: string;
}

const Restaurants = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const cart = useSelector((state: any) => state?.cart?.selectedCart);
  const cartId = cart.id || location.state.cart.cartId;
  const [selectedOption, setSelectedOption] = useState<ItemOptionType | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [itemError, setItemError] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<ItemOptionType[]>([]);
  const [removedResIds, setRemovedResIds] = useState<string[]>([]);
  const {
    restaurants: restaurantsData,
    isLoading: isRestaurantsLoading,
    error: restaurantsError,
  } = useRestaurantDetails();
  const [trigger, { data: cartData, isLoading: isFetching }] =
    useLazyGetCartDetailsByIdQuery();

  const isRestDataFetching = isRestaurantsLoading;

  const restaurantsById = useMemo(
    () =>
      new Map<string, FirebaseRestaurantItem>(
        (restaurantsData || []).map((restaurant) => [restaurant.id, restaurant])
      ),
    [restaurantsData]
  );

  const restaurants = useMemo(() => {
    if (!restaurantsData) return [];
    const restaurantIds = cartData?.restaurantIds || [];
    return restaurantsData
      .filter((restaurant: FirebaseRestaurantItem) => {
        return (
          (!selectedItems.some((selected) => selected.id === restaurant.id) &&
            !restaurantIds.includes(restaurant.id)) ||
          removedResIds.includes(restaurant.id)
        );
      })
      .map((item: FirebaseRestaurantItem) => ({
        value: item.displayName,
        label: (
          <div className="d-flex gap-2">
            <img
              src={item.imageUrl}
              alt={item.displayName}
              width="30px"
              height="30px"
            />
            {item.displayName}
          </div>
        ),
      }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantsData, selectedItems, cartData]);

  const handleItemChange = (option: any) => {
    if (option) {
      const selectedRestaurant = (restaurantsData || []).find(
        (item: ItemOptionType) => item.displayName === option.value
      );
      if (selectedRestaurant) {
        setSelectedOption(selectedRestaurant);
      }
      setItemError(null);
    }
  };

  const combinedItems = useMemo(() => {
    const existingRestaurants = (cartData?.restaurantIds || []).map(
      (restaurantId: string) => {
        const foundRestaurant = restaurantsById.get(restaurantId);

        if (foundRestaurant) {
          return {
            id: foundRestaurant.id,
            displayName: foundRestaurant.displayName,
            imageUrl: foundRestaurant.imageUrl,
          };
        }

        return {
          id: restaurantId,
          displayName: restaurantId,
          imageUrl: "",
        };
      }
    );

    const uniqueById = new Map<string, ItemOptionType>();
    [...selectedItems, ...existingRestaurants].forEach((item) => {
      uniqueById.set(item.id, item);
    });

    return Array.from(uniqueById.values());
  }, [selectedItems, cartData?.restaurantIds, restaurantsById]);

  const handleRemoveRestaurant = (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        setRemovedResIds((prev) => [...prev, id]);
        setSelectedItems((prevItems) =>
          prevItems.filter((item) => item.id !== id)
        );

        Swal.fire("Deleted!", "The restaurant has been removed.", "success");
      }
    });
  };
  const handleAdd = () => {
    if (selectedOption) {
      setSelectedItems([...selectedItems, selectedOption]);
      setSelectedOption(null);
      setItemError(null);
    }
  };
  useEffect(() => {
    if (cartId) {
      trigger(cartId);
    }
    // if (cartData?.reasonForBeingOffline) {
    //   setSelectedReason(cartData.reasonForBeingOffline);
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId, trigger]);

  useEffect(() => {
    if (restaurantsError) {
      setItemError("Unable to load restaurants from Firebase.");
    } else if (itemError === "Unable to load restaurants from Firebase.") {
      setItemError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantsError]);
  const [updateCartDetails] = useUpdateCartDetailsMutation();

  const handleSave = () => {
    if(cartData?.cartType === null || cartData?.cartType === undefined){
     toast.error("Cart type is missing. Please select a cart type before saving.");
     return;
    }
    let restList = selectedItems.map((item) => item.id);
    if (cartData?.restaurantIds) {
      restList.push(...cartData?.restaurantIds);
    }
    // restList.push(...cartData?.restaurantIds);
    restList = restList.filter((item) => !removedResIds?.includes(item));
    setIsLoading(true);
    updateCartDetails({
      cartId,
      restaurantIds: restList,
      cartType: cartData?.cartType,
    }).then(() => {
      setIsLoading(false);
      navigate("/carts/carts-list");
    });
  };
  return (
    <div>
      <Card className="mt-3">
        <CardHeader className="cart-view-card-header">
          <h4>Add Restaurants For Cart</h4>
        </CardHeader>
        <CardBody>
          <Row className="align-items-center">
            <Col sm={4}>
              <Label className="pt-1 form-label">Add Restaurants</Label>
            </Col>
            <Col sm={4}>
              <Select
                value={
                  selectedOption
                    ? {
                      value: selectedOption.displayName,
                      label: (
                        <div className="d-flex gap-2">
                          <img
                            src={selectedOption.imageUrl}
                            alt={selectedOption.displayName}
                            width="30px"
                            height="30px"
                          />
                          {selectedOption.displayName}
                        </div>
                      ),
                    }
                    : null
                }
                onChange={handleItemChange}
                options={restaurants}
                placeholder={(isLoading || isRestDataFetching) ? "Loading..." : "Search Restaurant"}
                loadingMessage={() => "Loading restaurants..."}
                noOptionsMessage={() =>
                  isLoading || isRestDataFetching
                    ? "Loading..."
                    : "No restaurants found"
                }
              />
            </Col>
            <Col sm={2} className="text-right">
              <ButtonComponentV2
                titel="+Add"
                type="button"
                onClick={handleAdd}
                isDisabled={!selectedOption}
                color="#fff"
                backgroundColor="#590B21"
              />
            </Col>
          </Row>
          {itemError && (
            <Row className="mt-2">
              <Col>
                <div className="text-danger">{itemError}</div>
              </Col>
            </Row>
          )}
          <hr />
          <h5>Selected Restaurants</h5>
          <Table bordered className="mt-3" responsive>
            <thead>
              <tr>
                <th>Name</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {isFetching || isRestDataFetching ? (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ height: "200px" }}
                >
                  <Spinner animation="border" variant="primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <span className="ms-2">Please Wait</span>
                </div>
              ) : (
                <>
                  {combinedItems
                    .filter((item) => !removedResIds?.includes(item.id))
                    .map((item, index) => (
                      <tr key={index}>
                        <td>{item.displayName}</td>
                        <td>
                          <Button
                            variant="light"
                            onClick={() => handleRemoveRestaurant(item.id)}
                          >
                            <FontAwesomeIcon icon={faTrash} color="#F24E1E" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                </>
              )}
            </tbody>
          </Table>
        </CardBody>
        <div className="d-flex justify-content-end align-items-center">
          {isLoading ? (
            <>
              <ButtonComponentV2
                titel="Saving..."
                icon={<FontAwesomeIcon icon={faSpinner} spin />}
                isDisabled={true}
                color="#fff"
                backgroundColor="#590B21"
              />
            </>
          ) : (
            <ButtonComponentV2
              titel="Save"
              icon={<FontAwesomeIcon icon={faFloppyDisk} />}
              onClick={handleSave}
              color="#fff"
              backgroundColor="#590B21"
            />
          )}
        </div>
      </Card>
    </div>
  );
};

export default Restaurants;
